import { t } from 'i18next';
import { useMemo } from 'react';
import type { ComboboxOption } from '../../../components/ui/combobox/types';
import { useCustomerGroups } from '../../customer-groups/hooks/use-customer-groups';
import { useSalesAgentsGroups } from '../../customers/hooks/use-sales-agent-groups';
import { useAllPriceLists } from '../../price-lists';
import { COMPANY_NET_TERMS } from '../section-context-schema';

/**
 * Combobox options for the shared company/section "context" fields
 * (customer group, price lists, sales-agent groups, net terms).
 */
export function useSectionContextOptions() {
  const { data: customerGroups } = useCustomerGroups();
  const { data: priceLists } = useAllPriceLists();
  const { data: salesAgentGroups } = useSalesAgentsGroups(true);

  const customerGroupOptions = useMemo<ComboboxOption[]>(
    () => customerGroups.map((group) => ({ value: group.slug, label: group.name })),
    [customerGroups],
  );
  const priceListOptions = useMemo<ComboboxOption[]>(
    () => priceLists.map((list) => ({ value: list.slug, label: list.name })),
    [priceLists],
  );
  const salesAgentGroupOptions = useMemo<ComboboxOption[]>(
    () => (salesAgentGroups ?? []).map((group) => ({ value: group, label: group })),
    [salesAgentGroups],
  );
  const netTermsOptions = useMemo<ComboboxOption[]>(
    () =>
      COMPANY_NET_TERMS.map((term) => ({
        value: term,
        label: t(
          `features:companies.pages.company_form_page.context.payment_on_account.net_terms_options.${term}`,
        ),
      })),
    [],
  );

  return { customerGroupOptions, priceListOptions, salesAgentGroupOptions, netTermsOptions };
}
